import * as React from 'react'
import { styled } from '@mui/material/styles'
import { useParams, Navigate, useLocation } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import View from 'proposals/components/layout/View'
import { useTitle } from 'utils/useTitle'
import { useRequestForProposalOpenedMutation } from 'proposals/requestForProposal/queries/useRequestForProposalMutations'
import { useProposalStateQuery } from 'proposals/proposal/queries/useProposalQuery'
import { useGetRequestForProposalByTokenQuery } from 'proposals/requestForProposal/queries/useRequestForProposalQuery'
import RequestForProposalBasicInfo from 'proposals/requestForProposal/RequestForProposalBasicInfo'
import ProposalIntent from 'proposals/proposal/ProposalIntent'
import ProposalDelegate from 'proposals/proposal/ProposalDelegate'
import ProposeFormContainer from 'proposals/proposal/ProposeFormContainer'
import RequestForProposalDownloadPdf from 'proposals/requestForProposal/RequestForProposalDownloadPdf'
import Box from '@mui/material/Box'
import ViewHeader from 'proposals/proposal/views/ViewHeader'
import { ViewLayout } from 'proposals/proposal/views/ViewLayout'
import { RequestForProposalStatus } from 'proposals/requestForProposal/requestForProposalConstants'
import ProposalStateRegister from 'proposals/proposal/ProposalStateRegister'
import ProposalStateEdit from 'proposals/proposal/ProposalStateEdit'
import ProposalProtectedInfo from 'proposals/proposal/ProposalProtectedInfo'
import { initTranslation } from 'app/translations/i18n'
import ProposalVendorPortalPromo from 'proposals/proposal/ProposalVendorPortalPromo'
import { useLeaveToVendorPortal } from 'proposals/proposal/useLeaveToVendorPortal'

const LoadingContainer = styled('div')(() => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center'
}))

type VendorPortalLocationState = {
  from?: string
}

const ProposeView: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const location = useLocation()
  const fromVendors = (location.state as VendorPortalLocationState | null)?.from === '/vendors'
  const [isDirty, setIsDirty] = React.useState(false)
  const leaveToVendorPortal = useLeaveToVendorPortal(isDirty)
  const handleDirtyChange = React.useCallback((dirty: boolean) => {
    setIsDirty(dirty)
  }, [])

  const { mutate: rfpOpened } = useRequestForProposalOpenedMutation()

  const { isLoading, isError, isSuccess, data } = useGetRequestForProposalByTokenQuery(token as string)

  const {
    isLoading: isLoadingProposeState,
    isError: isErrorProposeState,
    data: proposeState
  } = useProposalStateQuery(token as string)

  useTitle(data?.requestForProposal.title ?? '')

  const fields = React.useMemo(() => {
    if (!data) {
      return []
    }

    return data.requestForProposal.fields.sort((a, b) => a.order - b.order)
  }, [data])

  React.useEffect(() => {
    if (isSuccess && data) {
      initTranslation(data.requestForProposal.language)
      rfpOpened({
        rfpId: data.requestForProposal.id,
        recipientId: data.currentViewingRecipientId ?? ''
      })
    }
  }, [data, isSuccess, rfpOpened])

  if (!token || isError || isErrorProposeState) {
    return <Navigate to="/error" replace />
  }

  if (isLoading || !data || isLoadingProposeState) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  const { requestForProposal, ...recipient } = data

  const recipientCompany =
    recipient && recipient.currentViewingRecipientBusinessId
      ? { name: recipient.currentViewingRecipientCompanyName, businessId: recipient.currentViewingRecipientBusinessId }
      : null

  return (
    <View>
      <ViewLayout>
        <ViewHeader
          title={requestForProposal.title}
          logoUrl={requestForProposal.tenantLogoUrl}
          onBack={fromVendors ? leaveToVendorPortal : undefined}
        />
        <Box sx={{ gridArea: 'info', mt: 2 }}>
          <RequestForProposalBasicInfo data={requestForProposal} />
          <RequestForProposalDownloadPdf rfpId={requestForProposal.id} rfpTitle={requestForProposal.title} />
        </Box>
        {data.requestForProposal.status !== RequestForProposalStatus.CLOSED && (
          <Box
            displayPrint={proposeState?.hasProposed ? 'none' : undefined}
            sx={{ gridArea: 'side-function' }}
          >
            {!proposeState?.hasProposed && recipient && !proposeState?.initialAnswer && (
              <ProposalIntent recipientId={recipient.currentViewingRecipientId} rfpId={requestForProposal.id} />
            )}
            {!proposeState?.hasProposed && recipient && data.canDelegate && (
              <ProposalDelegate rfpId={requestForProposal.id} token={token} />
            )}
            {proposeState?.hasProposed && !proposeState?.hasBidderContactRegistered && (
              <ProposalStateRegister
                bidderContactEmail={proposeState.bidderContactEmail}
                proposalTS={proposeState.proposalTS}
              />
            )}
            {proposeState?.hasProposed && proposeState?.hasBidderContactRegistered && (
              <ProposalStateEdit
                bidderContactEmail={proposeState.bidderContactEmail}
                proposalTS={proposeState.proposalTS}
              />
            )}
            {requestForProposal.proposalViewingLockedUntilDueDate && (
              <ProposalProtectedInfo isProtected={requestForProposal.proposalViewingLockedUntilDueDate} />
            )}
            {!fromVendors && <ProposalVendorPortalPromo onGoToPortal={leaveToVendorPortal} />}
          </Box>
        )}
        <ProposeFormContainer
          fields={fields}
          recipient={recipient}
          hasPropose={proposeState?.hasProposed}
          isClosed={data.requestForProposal.status === RequestForProposalStatus.CLOSED}
          recipientCompany={recipientCompany}
          rfpLang={data.requestForProposal.language}
          region={data.currentViewingRecipientTenantRegion}
          onDirtyChange={handleDirtyChange}
        />
      </ViewLayout>
    </View>
  )
}

export default ProposeView
